import React, { useState, useEffect } from 'react'
import { useParams, useLocation, Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import GallerySection from '../components/GallerySection'
import SubpageFaqSection from '../components/SubpageFaqSection'
import NotFoundPage from './NotFoundPage'
import { assetPath } from '../lib/images'
import { MENU_ITEMS } from '../lib/nav'
import { getSubpageFaq } from '../lib/subpageFaqs'
import { HanddrawnArrow } from '../components/HanddrawnArrow'
import { ArcOrbitButton } from '../components/ArcOrbitButton'
import { useSmartSticky } from '../hooks/useSmartSticky'

// Robust slug helper handling both object and string slugs
const getEntrySlug = (item) => {
  if (!item) return ''
  if (typeof item.slug === 'string') return item.slug
  if (typeof item.slug === 'object' && item.slug !== null) {
    return item.slug._ || item.slug.da || item.slug.en || Object.values(item.slug)[0] || ''
  }
  return ''
}

export default function CmsPage({ fixedSlug = null }) {
  const location = useLocation()
  const params = useParams()
  const sidebarRef = useSmartSticky({ topOffset: 104, bottomOffset: 32 })

  const [page, setPage] = useState(null)
  const [allPages, setAllPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [expandedSections, setExpandedSections] = useState({})

  const toggleSidebarSection = (title) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }))
  }

  // Automatisk åbning af den sektion og eventuelle indre dropdown, som den aktuelle underside tilhører
  useEffect(() => {
    const current = location.pathname
    const newExpanded = {}

    MENU_ITEMS.forEach((group) => {
      if (group.children && group.children.length > 0) {
        const matchesGroup = group.children.some(
          (sub) => sub.href === current || (sub.children && sub.children.some((c) => c.href === current))
        )
        if (matchesGroup) {
          newExpanded[group.label] = true

          group.children.forEach((sub) => {
            if (sub.children && sub.children.length > 0) {
              const matchesNested = sub.href === current || sub.children.some((c) => c.href === current)
              if (matchesNested) {
                newExpanded[`${group.label}_${sub.label}`] = true
              }
            }
          })
        }
      }
    })

    setExpandedSections(newExpanded)
  }, [location.pathname])

  const getDerivedSlug = () => {
    if (fixedSlug) return fixedSlug
    const cleanPath = location.pathname.replace(/^\/+|\/+$/g, '')
    const pathSlug = cleanPath.replace(/\//g, '-')
    return pathSlug || params.slug || ''
  }

  const targetSlug = getDerivedSlug()

  useEffect(() => {
    setLoading(true)
    setNotFound(false)

    fetch('/_api/public/content/pages')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch pages')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setAllPages(data)

          const cleanPath = location.pathname.replace(/^\/+|\/+$/g, '')
          const pathSlug = cleanPath.replace(/\//g, '-')

          let match = data.find((p) => {
            const s = getEntrySlug(p)
            return s === targetSlug || s === pathSlug || (params.slug && s === params.slug)
          })

          if (!match && (targetSlug === 'kontakt' || pathSlug === 'kontakt')) {
            match = data.find((p) => {
              const s = getEntrySlug(p)
              return s === 'kontakt-side' || s === 'kontakt'
            })
          }
          if (!match && (targetSlug === 'rundvisning' || pathSlug === 'rundvisning')) {
            match = data.find((p) => getEntrySlug(p) === 'rundvisning')
          }
          if (!match) {
            const segments = targetSlug.split('-')
            const lastSegment = segments[segments.length - 1].toLowerCase()
            match = data.find((p) => {
              const s = (getEntrySlug(p) || '').toLowerCase()
              const title = (p.title || '').toLowerCase()
              return (
                s === lastSegment ||
                s.endsWith(lastSegment) ||
                title === lastSegment ||
                (lastSegment === 'krea' && (s.includes('krea') || s.includes('trae'))) ||
                (lastSegment === 'fitness' && (s.includes('fitness') || s.includes('sport'))) ||
                (lastSegment === 'spring' && (s.includes('spring') || s.includes('outdoor'))) ||
                (lastSegment === 'mediefag' && (s.includes('medie') || s.includes('madlavning'))) ||
                (lastSegment === 'musik' && (s.includes('musik') || s.includes('metal'))) ||
                (lastSegment === 'selvforsvar' && (s.includes('selvforsvar') || s.includes('bullet-journal')))
              )
            })
          }

          if (match) {
            setPage(match)
          } else {
            setNotFound(true)
          }
        } else {
          setNotFound(true)
        }
      })
      .catch((err) => {
        console.error('Page fetch error:', err)
        setNotFound(true)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [location.pathname, targetSlug])

  if (loading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center py-28 text-[#36454F]">
        <i className="fa-solid fa-circle-notch fa-spin text-4xl text-[#007A53] mb-4" aria-hidden="true" />
        <p className="text-base font-bold font-['Plus_Jakarta_Sans']">Henter indhold...</p>
      </div>
    )
  }

  if (notFound || !page) {
    return <NotFoundPage />
  }

  const rawHero = Array.isArray(page.hero_image) && page.hero_image[0]
    ? page.hero_image[0]
    : page.hero_image
  const heroImg = assetPath(rawHero) || '/_assets/emmerske-efterskole-11_b9da62a8.jpg'

  const currentSlug = getEntrySlug(page)
  const isUndervisning =
    targetSlug === 'undervisning' || currentSlug === 'undervisning' || location.pathname === '/undervisning'

  const subjectTitles = {
    'undervisning-i-dansk': 'Undervisning i dansk',
    'undervisning-i-matematik': 'Undervisning i matematik',
    'undervisning-i-biologi': 'Undervisning i biologi',
    'undervisning-i-engelsk': 'Undervisning i engelsk',
    'undervisning-i-fysik-kemi': 'Undervisning i fysik & kemi',
    'undervisning-i-geografi': 'Undervisning i geografi',
    'undervisning-i-idraet': 'Undervisning i idræt',
    'undervisning-i-historie': 'Undervisning i historie',
    'undervisning-i-samfundsfag': 'Undervisning i samfundsfag',
    'undervisning-i-kristendom': 'Undervisning i kristendom',
    'undervisning-i-tysk': 'Undervisning i tysk',
  }
  const isFagPage = Boolean(
    subjectTitles[targetSlug] || subjectTitles[currentSlug] || location.pathname.startsWith('/undervisning-i-')
  )
  const fagTitle = subjectTitles[targetSlug] || subjectTitles[currentSlug] || (page ? page.title : '')
  const faqData = getSubpageFaq(targetSlug) || getSubpageFaq(currentSlug)

  return (
    <div className="w-full bg-white">
      <SeoHead
        title={page.meta_title || page.title}
        description={page.meta_description}
        image={heroImg}
      />

      {/* Underside Hero Banner – Filmisk, afrundet organisk design */}
      <section className="relative bg-[#36454F] text-white pt-36 sm:pt-44 lg:pt-52 pb-24 sm:pb-32 lg:pb-36 min-h-[440px] sm:min-h-[500px] flex items-center overflow-hidden">
        {/* Baggrundsbillede med mørk gradient */}
        <div className="absolute inset-0 z-0">
          <img
            src={heroImg}
            alt=""
            aria-hidden="true"
            className={`w-full h-full object-cover ${
              currentSlug === 'undervisning-valgfag-cykling' || currentSlug === 'cykling'
                ? 'object-[48%_32%]'
                : 'object-center'
            }`}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, rgba(54,69,79,0.85) 0%, rgba(54,69,79,0.65) 50%, rgba(47,111,115,0.45) 100%)',
            }}
          />
        </div>

        {/* Subtil organisk form i hjørnet */}
        <div className="background-blob -bottom-24 -right-24 bg-white/10" aria-hidden="true" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          {/* Eyebrow i Caveat håndskrift */}
          <span className="font-['Shadows_Into_Light'] text-2xl sm:text-3xl text-[#DCEEF2] -rotate-2 inline-block mb-2 drop-shadow-sm">
            {isUndervisning
              ? 'Læs mere om fagene'
              : isFagPage
              ? 'Vi møder dig som du er'
              : currentSlug === 'dld'
              ? 'Udviklingsmæssig sprogforstyrrelse'
              : currentSlug === 'apd'
              ? 'Auditiv processeringsforstyrrelse'
              : currentSlug === 'undervisning-valgfag'
              ? 'For den kreative såvel som den aktive'
              : 'Emmerske Efterskole'}
          </span>

          {/* Stor tung overskrift */}
          <h1 className="font-['Plus_Jakarta_Sans'] text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black text-white tracking-tight leading-[1.1] max-w-4xl drop-shadow-md">
            {isFagPage
              ? fagTitle
              : currentSlug === 'undervisning-valgfag'
              ? 'Valgfag på Emmerske'
              : page.title}
          </h1>

          {page.subtitle &&
            !isUndervisning &&
            !isFagPage &&
            currentSlug !== 'ordblindeefterskole' &&
            currentSlug !== 'undervisning-valgfag' && (
              <p className="mt-4 text-[#DCEEF2] text-base sm:text-lg lg:text-xl font-medium leading-relaxed max-w-3xl">
                {page.subtitle}
              </p>
            )}

          {/* Organisk CTA-række i heroen */}
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <ArcOrbitButton
              to="/rundvisning"
              icon={<i className="fa-solid fa-calendar-check" aria-hidden="true" />}
            >
              Book rundvisning
            </ArcOrbitButton>
            <a
              href="tel:74724433"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/20 hover:bg-white text-white hover:text-[#36454F] font-bold text-sm backdrop-blur-md border border-white/40 transition-colors"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
              <span>74 72 44 33</span>
            </a>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          
          {/* Main Article Content (Left, 8 cols on desktop) */}
          <div className="lg:col-span-8 min-w-0">
            <article className="py-2">
              <div
                className="cms-prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html: (page.content || '').replace(
                    /<h3>Har du lyst til at se skolen\?<\/h3>([\s\S]*?)<\/p>/i,
                    `<h3>Har du lyst til at se skolen?</h3><div class="my-6"><a href="/rundvisning" class="organic-button !py-3 !px-7 text-sm inline-flex items-center gap-2"><i class="fa-solid fa-calendar-check" aria-hidden="true"></i><span>Book en personlig rundvisning</span></a></div>`
                  ),
                }}
              />

              {/* Organiske CTA-knapper: Skriv til os & Ring til os */}
              <div className="mt-10 pt-8 border-t border-[#DCEEF2] flex flex-wrap items-center gap-4">
                <ArcOrbitButton
                  href="mailto:ee@eeskole.dk"
                  icon={<i className="fa-solid fa-envelope" aria-hidden="true" />}
                >
                  Skriv til os
                </ArcOrbitButton>
                <a
                  href="tel:74724433"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-[#2F6F73] text-[#2F6F73] hover:bg-[#DCEEF2]/60 font-bold text-sm transition-all hover:scale-103"
                >
                  <i className="fa-solid fa-phone text-[#007A53]" aria-hidden="true" />
                  <span>Ring 74 72 44 33</span>
                </a>
              </div>

              {/* YouTube video embedding på Om os undersiden */}
              {(currentSlug === 'om-os' || currentSlug === 'om-os-2' || location.pathname === '/om-os') && (
                <div className="mt-10 rounded-3xl overflow-hidden shadow-xl bg-black aspect-video relative border-4 border-white">
                  <iframe
                    src="https://www.youtube-nocookie.com/embed/Mz_9J2tl5Ug?rel=0"
                    title="Emmerske Efterskole præsentation"
                    className="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>
              )}
            </article>

            {/* FAQ Accordion Section from Scrape */}
            {faqData && <SubpageFaqSection faq={faqData} />}

            {/* Maps sektion nederst: Undervisning & Efterskolernes Dag */}
            {(targetSlug === 'undervisning' ||
              location.pathname === '/undervisning' ||
              targetSlug === 'efterskolernes-dag-2026' ||
              location.pathname === '/efterskolernes-dag-2026') && (
              <div className="mt-16 w-full">
                <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-[#DCEEF2] grid grid-cols-1 lg:grid-cols-12 items-stretch">
                  <div className="lg:col-span-6 relative min-h-[300px] sm:min-h-[350px] bg-[#007A53] overflow-hidden">
                    <iframe
                      title="Emmerske Efterskole placering"
                      src="https://maps.google.com/maps?q=54.94586,8.89885&z=14&output=embed&iwloc="
                      width="100%"
                      height="100%"
                      className="absolute inset-0 w-full h-full border-0 filter saturate-150 contrast-125 opacity-40 mix-blend-luminosity"
                      loading="lazy"
                      aria-hidden="true"
                    />
                    <div className="absolute inset-0 bg-[#007A53]/85 mix-blend-multiply pointer-events-none" />
                    <div className="absolute inset-0 flex items-center justify-center p-6 z-10 pointer-events-none">
                      <img
                        src="/_assets/kort-emmerske.png"
                        alt="Danmarkskort med markering af Emmerske Efterskole"
                        className="w-full h-full max-h-[280px] sm:max-h-[320px] object-contain drop-shadow-2xl"
                      />
                    </div>
                  </div>

                  <div className="lg:col-span-6 p-6 sm:p-10 flex flex-col justify-center space-y-4 bg-white">
                    <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-black text-[#36454F] tracking-tight">
                      Bliv klar til uddannelse
                    </h3>
                    <p className="text-[#36454F]/85 text-base leading-relaxed">
                      Vores fokus er, at du som elev på Emmerske Efterskole bliver uddannelsesparat. Book en
                      rundvisning i dag og få meget mere at vide om skolen.
                    </p>
                    <div className="pt-2">
                      <ArcOrbitButton
                        to="/rundvisning"
                        icon={<i className="fa-solid fa-calendar-check" aria-hidden="true" />}
                      >
                        Book rundvisning
                      </ArcOrbitButton>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Sidebar Menu (4 cols on desktop) */}
          <aside ref={sidebarRef} className="lg:col-span-4 space-y-6 smart-sticky-sidebar">
            <div className="bg-[#DCEEF2]/60 rounded-3xl p-6 sm:p-7 shadow-sm border border-[#DCEEF2] space-y-6">
              <div className="border-b border-[#2F6F73]/20 pb-4">
                <span className="font-['Shadows_Into_Light'] text-xl text-[#2F6F73] block mb-1">
                  Udforsk skolen
                </span>
                <h3 className="font-['Plus_Jakarta_Sans'] text-xl font-black text-[#36454F]">
                  Undersider & Emner
                </h3>
              </div>

              <nav className="space-y-3" aria-label="Hovednavigation og undersider">
                {MENU_ITEMS.map((group) => {
                  const isTopActive = location.pathname === group.href
                  const hasSub = group.children && group.children.length > 0
                  const isExpanded = !!expandedSections[group.label]

                  return (
                    <div key={group.label} className="space-y-1">
                      {hasSub ? (
                        <button
                          type="button"
                          onClick={() => toggleSidebarSection(group.label)}
                          className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-2xl text-sm font-extrabold text-left transition-all cursor-pointer ${
                            isTopActive || group.children.some((s) => s.href === location.pathname)
                              ? 'bg-[#007A53] text-white shadow-md'
                              : isExpanded
                              ? 'text-[#36454F] bg-white'
                              : 'text-[#36454F] hover:bg-white/80'
                          }`}
                        >
                          <span>{group.label}</span>
                          <i
                            className={`fa-solid fa-chevron-down text-xs transition-transform duration-200 ${
                              isExpanded ? 'rotate-180' : ''
                            }`}
                            aria-hidden="true"
                          />
                        </button>
                      ) : group.external ? (
                        <a
                          href={group.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between px-3.5 py-2.5 rounded-2xl text-sm font-bold text-[#36454F] hover:bg-white/80 transition-colors"
                        >
                          <span>{group.label}</span>
                          <i className="fa-solid fa-arrow-up-right-from-square text-xs text-[#2F6F73]/60" />
                        </a>
                      ) : (
                        <Link
                          to={group.href}
                          className={`block px-3.5 py-2.5 rounded-2xl text-sm font-extrabold transition-all ${
                            isTopActive
                              ? 'bg-[#007A53] text-white shadow-md'
                              : 'text-[#36454F] hover:bg-white/80'
                          }`}
                        >
                          {group.label}
                        </Link>
                      )}

                      {/* Dropdown underpunkter */}
                      {hasSub && isExpanded && (
                        <div className="ml-3 pl-3 border-l-2 border-[#007A53]/30 space-y-1 py-1">
                          {group.children.map((sub) => {
                            const isSubActive = location.pathname === sub.href
                            const hasNested = sub.children && sub.children.length > 0

                            return (
                              <div key={sub.label}>
                                {sub.external ? (
                                  <a
                                    href={sub.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between px-3 py-1.5 text-xs font-bold text-[#36454F] hover:text-[#007A53] rounded-lg"
                                  >
                                    <span>{sub.label}</span>
                                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px] text-[#2F6F73]/50" />
                                  </a>
                                ) : (
                                  <Link
                                    to={sub.href}
                                    className={`block px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                      isSubActive
                                        ? 'bg-[#007A53] text-white font-black'
                                        : 'font-bold text-[#36454F] hover:text-[#007A53] hover:bg-white/60'
                                    }`}
                                  >
                                    {sub.label}
                                  </Link>
                                )}

                                {hasNested && (
                                  <div className="ml-2.5 pl-2 border-l border-[#007A53]/20 space-y-0.5 my-1">
                                    {sub.children.map((nested) => {
                                      const isNestedActive = location.pathname === nested.href
                                      return (
                                        <Link
                                          key={nested.label}
                                          to={nested.href}
                                          className={`block px-2.5 py-1 text-[11px] rounded transition-colors ${
                                            isNestedActive
                                              ? 'text-[#007A53] font-black underline'
                                              : 'text-[#536363] hover:text-[#007A53]'
                                          }`}
                                        >
                                          {nested.label}
                                        </Link>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </nav>

              {/* Hurtig kontakt i sidebar */}
              <div className="pt-4 border-t border-[#2F6F73]/20 space-y-3">
                <ArcOrbitButton
                  to="/rundvisning"
                  icon={<i className="fa-solid fa-calendar-check" />}
                  arrow={false}
                  className="w-full !min-h-[48px]"
                >
                  Book rundvisning
                </ArcOrbitButton>
                <a
                  href="tel:74724433"
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-full bg-white text-[#36454F] hover:text-[#007A53] text-xs font-extrabold shadow-sm transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#007A53]" />
                  <span>Ring 74 72 44 33</span>
                </a>
              </div>
          </div>
        </aside>
      </div>
    </main>

      {/* Særligt Galleri på Undervisnings-, DLD-, APD- og Fællesskabssider */}
      {(isUndervisning || currentSlug === 'hverdagen' || currentSlug === 'faciliteter' || currentSlug === 'dld' || currentSlug === 'apd') && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 w-full">
          <GallerySection
            galleryRef={(currentSlug === 'dld' || currentSlug === 'apd') ? 'faellesskab' : (page.gallery_ref || 'undervisning')}
            customTitle={(currentSlug === 'dld' || currentSlug === 'apd') ? 'Glade elever og stærkt fællesskab' : 'Glimt fra undervisning og værksteder'}
            customDescription={(currentSlug === 'dld' || currentSlug === 'apd') ? 'Oplev hverdagen, relationerne og den trygge stemning på Emmerske Efterskole.' : 'Oplev hverdagen, det praktiske arbejde og vores dedikerede faglokaler.'}
          />
        </section>
      )}
    </div>
  )
}
